import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Clock, 
  FileText, 
  RefreshCw, 
  ChevronRight, 
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Building2,
  Mail,
  Calendar,
  Loader2,
  ChevronDown,
  Send,
  XCircle,
  ShieldAlert,
  FileCheck
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/currency';

// Investment state configuration
const INVESTMENT_STATES = {
  INTEREST: { 
    label: 'Interest Registered', 
    step: 1, 
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    actionLabel: 'Pay EOI',
    actionType: 'payment'
  },
  EOI_PAID: { 
    label: 'EOI Paid', 
    step: 2, 
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    actionLabel: 'Waiting for Documents',
    actionType: 'waiting'
  },
  DOC_SENT: { 
    label: 'Documents Sent', 
    step: 3, 
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    actionLabel: 'Sign in Email',
    actionType: 'email'
  },
  DOC_SIGNED: { 
    label: 'Documents Signed', 
    step: 4, 
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    actionLabel: 'Pay Capital',
    actionType: 'payment'
  },
  CAPITAL_PAID: { 
    label: 'Capital Paid', 
    step: 5, 
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    actionLabel: 'Awaiting Closure',
    actionType: 'waiting'
  },
  COMPLETED: { 
    label: 'Completed', 
    step: 6, 
    color: 'text-[#4ADE80]',
    bgColor: 'bg-[#4ADE80]/20',
    actionLabel: 'Investment Active',
    actionType: 'none'
  },
  REFUNDED_EOI: {
    label: 'EOI Refunded',
    step: 2,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    actionLabel: 'Refund Processed',
    actionType: 'none'
  },
  REFUNDED_FULL: {
    label: 'Fully Refunded',
    step: 5,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    actionLabel: 'Refund Processed',
    actionType: 'none'
  },
  CANCELLATION_REQUESTED: {
    label: 'Cancellation Pending',
    step: 0,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    actionLabel: 'Under Admin Review',
    actionType: 'none'
  },
  CANCELLED: {
    label: 'Cancelled',
    step: 0,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    actionLabel: 'Investment Cancelled',
    actionType: 'none'
  },
};

export default function InvestorPortfolioTab({ user }) {
  const [investments, setInvestments] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('pending');
  const [resendingEmail, setResendingEmail] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      // Fetch investments AND centralized metrics in parallel
      const [investmentsRes, metricsRes] = await Promise.all([
        api.get('/investor/my-investments'),
        user?.id ? api.get(`/metrics/investor/${user.id}`) : Promise.resolve({ data: null })
      ]);

      const data = investmentsRes.data?.investments || [];
      setInvestments(data);
      if (metricsRes.data) setMetricsData(metricsRes.data);
    } catch (error) {
      console.error('Error fetching investments:', error);
      setInvestments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (investmentId) => {
    setResendingEmail(investmentId);
    try {
      await api.post(`/investor/investments/${investmentId}/resend-documents`);
      toast.success('Document re-send request submitted successfully');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to resend documents';
      toast.error(msg);
    } finally {
      setResendingEmail(null);
    }
  };

  const handleCancelRequest = (investment) => {
    setCancelModal(investment);
    setCancelReason('');
    setCancelDetails('');
  };

  const submitCancellation = async () => {
    if (!cancelModal || !cancelReason) return;
    setCancelSubmitting(true);
    try {
      await api.post(
        `/investor/${user?.id || cancelModal.investorId}/investments/${cancelModal.investmentId}/request-cancellation`,
        { reason: cancelReason, details: cancelDetails || undefined }
      );
      toast.success('Cancellation request submitted. Awaiting admin approval.');
      setCancelModal(null);
      fetchInvestments();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit cancellation';
      toast.error(msg);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const now = new Date();
    const target = new Date(dateStr);
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Categorize investments
  const terminalCancelStates = ['CANCELLED', 'REFUNDED_EOI', 'REFUNDED_FULL'];
  const pendingInvestments = investments.filter(inv => !['COMPLETED', ...terminalCancelStates].includes(inv.state));
  const completedInvestments = investments.filter(inv => inv.state === 'COMPLETED');
  const refundedInvestments = investments.filter(inv => terminalCancelStates.includes(inv.state));

  // Get current tab investments
  const currentInvestments = activeSubTab === 'pending' ? pendingInvestments : activeSubTab === 'completed' ? completedInvestments : refundedInvestments;

  // Calculate totals from centralized metrics (SSoT) — fallback to local calc if metrics unavailable
  const confirmedStates = ['DOC_SIGNED', 'CAPITAL_PAID', 'COMPLETED'];
  const totalCommitment = metricsData?.totalCommitted ?? investments.reduce((sum, inv) => sum + (inv.amounts?.totalCommitment || 0), 0);
  const totalInvested = metricsData?.totalInvested ?? investments
    .filter(inv => confirmedStates.includes(inv.state))
    .reduce((sum, inv) => sum + (inv.amounts?.capitalPaid || inv.amounts?.totalCommitment || 0), 0);
  const totalInEscrow = pendingInvestments
    .filter(inv => confirmedStates.includes(inv.state))
    .reduce((sum, inv) => sum + (inv.amounts?.capitalPaid || 0), 0);

  // Get urgency level for action required items
  const getUrgencyLevel = (investment) => {
    if (investment.state === 'DOC_SENT') {
      const daysLeft = getDaysUntil(investment.documents?.expiresAt);
      if (daysLeft !== null && daysLeft <= 3) return 'urgent';
      if (daysLeft !== null && daysLeft <= 7) return 'warning';
    }
    if (investment.state === 'DOC_SIGNED') return 'warning';
    return 'normal';
  };

  // Investment Timeline Component
  const InvestmentTimeline = ({ investment }) => {
    const currentStep = INVESTMENT_STATES[investment.state]?.step || 1;
    const steps = [
      { key: 'eoi', label: 'EOI Paid', amount: investment.amounts?.eoiAmount, date: investment.escrow?.eoiPaidAt },
      { key: 'docs_sent', label: 'Docs Sent', date: investment.documents?.sentAt },
      { key: 'signed', label: 'Signed', date: investment.documents?.signedAt },
      { key: 'capital', label: 'Capital Paid', amount: investment.amounts?.capitalAmount, date: investment.escrow?.capitalPaidAt },
      { key: 'complete', label: 'Complete', date: investment.completedAt },
    ];

    return (
      <div className="flex items-center justify-between w-full py-4">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep - 1;
          const isCurrent = index === currentStep - 1;
          const isPending = index >= currentStep;
          
          return (
            <div key={step.key} className="flex flex-col items-center flex-1 relative">
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className={`absolute top-3 left-1/2 w-full h-0.5 ${
                  isCompleted ? 'bg-[#4ADE80]' : 'bg-white/10'
                }`} />
              )}
              
              {/* Step circle */}
              <div className={`relative z-10 w-6 h-6 flex items-center justify-center ${
                isCompleted ? 'bg-[#4ADE80]' : 
                isCurrent ? 'bg-yellow-500 animate-pulse' : 
                'bg-white/10'
              }`}>
                {isCompleted ? (
                  <CheckCircle2 className="w-4 h-4 text-black" />
                ) : isCurrent ? (
                  <Clock className="w-3 h-3 text-black" />
                ) : (
                  <div className="w-2 h-2 bg-white/30" />
                )}
              </div>
              
              {/* Label */}
              <p className={`text-[10px] mt-2 text-center ${
                isCompleted ? 'text-[#4ADE80]' : 
                isCurrent ? 'text-yellow-400 font-medium' : 
                'text-gray-600'
              }`}>
                {step.label}
              </p>
              
              {/* Amount/Date */}
              {step.date && (
                <p className="text-[9px] text-gray-500 mt-0.5">
                  {formatDate(step.date)}
                </p>
              )}
              {step.amount && isPending && (
                <p className="text-[9px] text-gray-500 mt-0.5 font-mono">
                  {formatINR(step.amount)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Investment Card Component
  const InvestmentCard = ({ investment }) => {
    const stateConfig = INVESTMENT_STATES[investment.state];
    const isExpanded = expandedCard === investment.investmentId;
    const daysLeft = getDaysUntil(investment.documents?.expiresAt);
    const urgency = getUrgencyLevel(investment);

    const urgencyStyles = {
      urgent: 'border-red-500/50 bg-red-500/5',
      warning: 'border-yellow-500/30 bg-yellow-500/5',
      normal: 'border-white/10 hover:border-white/20',
    };

    return (
      <motion.div
        layout
        className={`border overflow-hidden transition-colors ${urgencyStyles[urgency]}`}
      >
        {/* Header */}
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                {urgency === 'urgent' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-mono">
                    <AlertTriangle className="w-3 h-3" />
                    URGENT
                  </span>
                )}
                {urgency === 'warning' && investment.state === 'DOC_SIGNED' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] font-mono">
                    <CreditCard className="w-3 h-3" />
                    PAYMENT DUE
                  </span>
                )}
              </div>
              <h3 className="text-lg text-white font-medium">{investment.projectName}</h3>
              <p className="text-sm text-gray-500">{investment.unitName}</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 text-xs ${stateConfig.bgColor} ${stateConfig.color}`}>
                {stateConfig.label}
              </span>
              {daysLeft !== null && daysLeft <= 7 && investment.state === 'DOC_SENT' && (
                <p className={`text-xs mt-2 ${daysLeft <= 3 ? 'text-red-400' : 'text-yellow-400'}`}>
                  Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Financial Summary — Committed / Paid / Outstanding */}
          {(() => {
            const committed = investment.amounts?.totalCommitment || 0;
            const paid = investment.amounts?.capitalPaid || 0;
            const outstanding = Math.max(0, committed - paid);
            const paymentPct = committed > 0 ? Math.round((paid / committed) * 100) : 0;
            const uf = investment.unitFunding;
            const isLocked = investment.locked || investment.state === 'CANCELLATION_REQUESTED';

            return (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-white/5">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase" data-testid={`label-committed-${investment.investmentId}`}>Committed</p>
                    <p className="font-mono text-white">{formatINR(committed)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase" data-testid={`label-paid-${investment.investmentId}`}>Paid</p>
                    <p className="font-mono text-[#4ADE80]">{formatINR(paid)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase" data-testid={`label-outstanding-${investment.investmentId}`}>Outstanding</p>
                    <p className={`font-mono ${outstanding > 0 ? 'text-yellow-400' : 'text-[#4ADE80]'}`}>{formatINR(outstanding)}</p>
                  </div>
                </div>

                {/* Progress Bars */}
                <div className="space-y-3 mb-4">
                  {/* Payment Progress */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Your Payment</span>
                      <span className="text-gray-400 font-mono">{paymentPct}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#4ADE80] transition-all duration-500" style={{ width: `${paymentPct}%` }} />
                    </div>
                    <p className="text-[9px] text-gray-600 mt-0.5">{formatINR(paid)} of {formatINR(committed)}</p>
                  </div>

                  {/* Unit Funding Progress */}
                  {uf && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500">Unit Funding</span>
                        <span className="text-gray-400 font-mono">{uf.percent}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/10 overflow-hidden">
                        <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.min(uf.percent, 100)}%` }} />
                      </div>
                      <p className="text-[9px] text-gray-600 mt-0.5">{formatINR(uf.collected)} of {formatINR(uf.target)} collected</p>
                    </div>
                  )}
                </div>

                {/* Document Status */}
                {investment.documents && !['INTEREST', 'EOI_PAID', 'CANCELLED', 'REFUNDED_EOI', 'REFUNDED_FULL'].includes(investment.state) && (
                  <div className="mb-4 p-3 bg-blue-500/5 border border-blue-500/20">
                    <p className="text-xs font-medium text-gray-400 mb-2 flex items-center gap-1.5">
                      <FileCheck className="w-3 h-3" />
                      Documentation Status
                    </p>
                    <div className="space-y-1.5">
                      {investment.documents?.investorSigned && (
                        <div className="flex items-center gap-2 text-xs">
                          <CheckCircle2 className="w-3 h-3 text-[#4ADE80] flex-shrink-0" />
                          <span className="text-gray-300">Agreement signed by you</span>
                        </div>
                      )}
                      {investment.documents?.investorSigned && !investment.documents?.platformSigned && (
                        <>
                          <div className="flex items-center gap-2 text-xs">
                            <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                            <span className="text-yellow-400">Awaiting platform counter-signature</span>
                          </div>
                          <p className="text-[10px] text-gray-600 mt-2 pl-5">
                            Final signed documents issued after: 100% payment, 100% unit funding, and escrow release to LLP.
                          </p>
                        </>
                      )}
                      {investment.documents?.fullyExecuted && (
                        <div className="flex items-center gap-2 text-xs">
                          <CheckCircle2 className="w-3 h-3 text-[#4ADE80] flex-shrink-0" />
                          <span className="text-[#4ADE80]">Documents fully executed</span>
                        </div>
                      )}
                      {!investment.documents?.investorSigned && investment.state === 'DOC_SENT' && (
                        <div className="flex items-center gap-2 text-xs">
                          <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                          <span className="text-gray-300">Awaiting your signature</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cancellation Requested Banner */}
                {investment.state === 'CANCELLATION_REQUESTED' && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30" data-testid={`cancellation-pending-${investment.investmentId}`}>
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-yellow-400 font-medium">Cancellation Under Review</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Your cancellation request is being reviewed by admin. All actions on this investment are paused.
                        </p>
                        {investment.cancellation?.reason && (
                          <p className="text-[10px] text-gray-500 mt-1">
                            Reason: {investment.cancellation.reason.replace(/_/g, ' ')}
                          </p>
                        )}
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Expected refund: {formatINR(investment.cancellation?.refundAmount || paid)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cancelled Info */}
                {investment.state === 'CANCELLED' && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30" data-testid={`cancelled-info-${investment.investmentId}`}>
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-red-400 font-medium">Investment Cancelled</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Refund of {formatINR(investment.cancellation?.refundAmount || paid)} has been processed.
                        </p>
                        {investment.cancellation?.reason && (
                          <p className="text-[10px] text-gray-500 mt-1">
                            Reason: {investment.cancellation.reason.replace(/_/g, ' ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Investment Timeline */}
          <InvestmentTimeline investment={investment} />

          {/* Document Signing Prompt - For DOC_SENT state */}
          {investment.state === 'DOC_SENT' && (
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white font-medium mb-1">Sign Documents via Email</p>
                  <p className="text-sm text-gray-400 mb-3">
                    We've sent the investment documents to <span className="text-white">{investment.documents?.sentTo}</span> on {formatDate(investment.documents?.sentAt)}. 
                    Please check your inbox and sign the documents to proceed.
                  </p>
                  <button 
                    onClick={() => handleResendEmail(investment.investmentId)}
                    disabled={resendingEmail === investment.investmentId}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid={`resend-email-${investment.investmentId}`}
                  >
                    {resendingEmail === investment.investmentId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Resend Document Email
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Capital Payment CTA - For DOC_SIGNED state */}
          {investment.state === 'DOC_SIGNED' && !investment.locked && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <button 
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#4ADE80] text-black font-medium hover:bg-[#4ADE80]/90 transition-colors"
                data-testid={`pay-capital-${investment.investmentId}`}
              >
                <CreditCard className="w-4 h-4" />
                Pay Capital Contribution ({formatINR(investment.amounts?.capitalAmount)})
              </button>
            </div>
          )}

          {/* EOI Payment CTA - For INTEREST state */}
          {investment.state === 'INTEREST' && !investment.locked && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <button 
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#4ADE80] text-black font-medium hover:bg-[#4ADE80]/90 transition-colors"
                data-testid={`pay-eoi-${investment.investmentId}`}
              >
                <CreditCard className="w-4 h-4" />
                Pay EOI ({formatINR(investment.amounts?.eoiAmount)})
              </button>
            </div>
          )}

          {/* Estimated Closure for CAPITAL_PAID state */}
          {investment.state === 'CAPITAL_PAID' && investment.estimatedClosureDate && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-4 p-3 bg-white/5">
              <Calendar className="w-3 h-3" />
              <span>All payments complete. Estimated unit closure: <span className="text-white">{formatDate(investment.estimatedClosureDate)}</span></span>
            </div>
          )}

          {/* Completed Investment Info */}
          {investment.state === 'COMPLETED' && (
            <div className="flex items-center gap-2 text-xs text-[#4ADE80] mt-4 p-3 bg-[#4ADE80]/10">
              <CheckCircle2 className="w-4 h-4" />
              <span>Investment completed on {formatDate(investment.completedAt)}. Your ownership is now active.</span>
            </div>
          )}

          {/* Refund Info */}
          {['REFUNDED_EOI', 'REFUNDED_FULL'].includes(investment.state) && investment.refund && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-white font-medium">Refund Details</p>
                <span className={`text-xs px-2 py-0.5 ${
                  investment.refund.status === 'completed' ? 'bg-[#4ADE80]/10 text-[#4ADE80]' : 'bg-yellow-500/10 text-yellow-400'
                }`}>
                  {investment.refund.status === 'completed' ? 'Refund Processed' : 'Refund Pending'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-500 mb-1">Refund Amount</p>
                  <p className="font-mono text-[#4ADE80]">{formatINR(investment.refund.amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Processed On</p>
                  <p className="text-white">{formatDate(investment.refund.processedAt)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 mb-1">Reason</p>
                  <p className="text-gray-400">{investment.refund.reason}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Reference</p>
                  <p className="font-mono text-white">{investment.refund.referenceId}</p>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Investment Button */}
          {!['COMPLETED', 'CANCELLED', 'REFUNDED_EOI', 'REFUNDED_FULL', 'CANCELLATION_REQUESTED'].includes(investment.state) && (
            <button
              onClick={() => handleCancelRequest(investment)}
              className="w-full text-center text-xs text-red-400/70 hover:text-red-400 mt-3 py-1.5 transition-colors"
              data-testid={`cancel-investment-${investment.investmentId}`}
            >
              Cancel Investment
            </button>
          )}

          {/* Expand/Collapse for more details */}
          <button
            onClick={() => setExpandedCard(isExpanded ? null : investment.investmentId)}
            className="w-full flex items-center justify-center gap-1 mt-4 pt-3 text-xs text-gray-500 hover:text-white transition-colors border-t border-white/5"
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10 bg-white/[0.02]"
            >
              <div className="p-5 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs mb-1">Investment ID</p>
                  <p className="font-mono text-white">{investment.investmentId}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Unit ID</p>
                  <p className="font-mono text-white">{investment.unitId}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Created</p>
                  <p className="text-white">{formatDate(investment.createdAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">EOI Amount</p>
                  <p className={`font-mono ${investment.escrow?.eoiPaidAt ? 'text-[#4ADE80]' : 'text-gray-400'}`}>
                    {investment.escrow?.eoiPaidAt ? '✓ ' : ''}{formatINR(investment.amounts?.eoiAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Capital Amount</p>
                  <p className={`font-mono ${investment.escrow?.capitalPaidAt ? 'text-[#4ADE80]' : 'text-gray-400'}`}>
                    {investment.escrow?.capitalPaidAt ? '✓ ' : ''}{formatINR(investment.amounts?.capitalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Documents</p>
                  <p className={`font-mono ${investment.documents?.signedAt ? 'text-[#4ADE80]' : investment.documents?.sentAt ? 'text-yellow-400' : 'text-gray-400'}`}>
                    {investment.documents?.signedAt ? '✓ Signed' : investment.documents?.sentAt ? '⏳ Pending Signature' : 'Not Sent'}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#4ADE80] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="investor-portfolio">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-2">My Portfolio</h1>
          <p className="text-sm text-gray-500">Track your investments and complete pending actions</p>
        </div>
        <button 
          onClick={fetchInvestments}
          className="flex items-center gap-2 px-4 py-2 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-8">
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Committed</p>
          <p className="font-mono text-xl md:text-3xl font-light text-white">{formatINR(totalCommitment)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Capital Paid</p>
          <p className="font-mono text-xl md:text-3xl font-light text-[#4ADE80]">{formatINR(totalInvested)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">In Escrow</p>
          <p className="font-mono text-xl md:text-3xl font-light text-blue-400">{formatINR(totalInEscrow)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Properties</p>
          <p className="font-mono text-xl md:text-3xl font-light text-white">{investments.length}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-0 mb-8 border-b border-white/10">
        <button
          onClick={() => setActiveSubTab('pending')}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeSubTab === 'pending' 
              ? 'text-white' 
              : 'text-gray-500 hover:text-gray-300'
          }`}
          data-testid="pending-tab"
        >
          Pending
          {pendingInvestments.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-xs font-mono ${
              activeSubTab === 'pending' 
                ? 'bg-yellow-500/20 text-yellow-400' 
                : 'bg-white/10 text-gray-400'
            }`}>
              {pendingInvestments.length}
            </span>
          )}
          {activeSubTab === 'pending' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4ADE80]" />
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('completed')}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeSubTab === 'completed' 
              ? 'text-white' 
              : 'text-gray-500 hover:text-gray-300'
          }`}
          data-testid="completed-tab"
        >
          Completed
          {completedInvestments.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-xs font-mono ${
              activeSubTab === 'completed' 
                ? 'bg-[#4ADE80]/20 text-[#4ADE80]' 
                : 'bg-white/10 text-gray-400'
            }`}>
              {completedInvestments.length}
            </span>
          )}
          {activeSubTab === 'completed' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4ADE80]" />
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('refunded')}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeSubTab === 'refunded' 
              ? 'text-white' 
              : 'text-gray-500 hover:text-gray-300'
          }`}
          data-testid="refunded-tab"
        >
          Refunded
          {refundedInvestments.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-xs font-mono ${
              activeSubTab === 'refunded' 
                ? 'bg-gray-500/20 text-gray-300' 
                : 'bg-white/10 text-gray-400'
            }`}>
              {refundedInvestments.length}
            </span>
          )}
          {activeSubTab === 'refunded' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4ADE80]" />
          )}
        </button>
      </div>

      {/* Investment List */}
      <div className="space-y-4">
        {currentInvestments.length === 0 ? (
          <div className="border border-white/10 p-8 md:p-12 text-center">
            {activeSubTab === 'pending' ? (
              <>
                <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No Pending Investments</h3>
                <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                  All your investments are complete! Browse new opportunities to grow your portfolio.
                </p>
              </>
            ) : activeSubTab === 'completed' ? (
              <>
                <CheckCircle2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No Completed Investments Yet</h3>
                <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                  Complete your pending investments to see them here.
                </p>
              </>
            ) : (
              <>
                <RefreshCw className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No Refunded Investments</h3>
                <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                  Cancelled or refunded investments will appear here.
                </p>
              </>
            )}
          </div>
        ) : (
          currentInvestments
            .sort((a, b) => {
              if (activeSubTab === 'pending') {
                // Sort by urgency for pending: DOC_SENT first, then DOC_SIGNED, then others
                const urgencyOrder = { DOC_SENT: 0, DOC_SIGNED: 1, INTEREST: 2, EOI_PAID: 3, CAPITAL_PAID: 4 };
                return (urgencyOrder[a.state] ?? 99) - (urgencyOrder[b.state] ?? 99);
              }
              // Sort completed by completion date (newest first)
              return new Date(b.completedAt || 0) - new Date(a.completedAt || 0);
            })
            .map(investment => (
              <InvestmentCard key={investment.investmentId} investment={investment} />
            ))
        )}
      </div>

      {/* Empty State for No Investments at All */}
      {investments.length === 0 && (
        <div className="border border-[#4ADE80]/30 bg-[#4ADE80]/5 p-8 md:p-12 text-center">
          <Building2 className="w-12 h-12 text-[#4ADE80]/50 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">Start Your Investment Journey</h3>
          <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
            Browse our curated selection of fractional real estate opportunities and begin building your portfolio.
          </p>
          <button className="px-6 py-3 bg-[#4ADE80] text-black font-medium hover:bg-[#4ADE80]/90 transition-colors">
            Explore Properties
          </button>
        </div>
      )}

      {/* Cancel Investment Modal */}
      <AnimatePresence>
        {cancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => !cancelSubmitting && setCancelModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#111] border border-white/10 w-full max-w-md"
              onClick={e => e.stopPropagation()}
              data-testid="cancel-investment-modal"
            >
              <div className="p-6">
                <h3 className="text-lg text-white font-medium mb-1">Cancel Investment?</h3>
                <p className="text-xs text-gray-500 mb-5">This action cannot be undone once approved.</p>

                <div className="p-3 bg-white/5 mb-4 text-sm">
                  <p className="text-gray-400">{cancelModal.projectName} — {cancelModal.unitName}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div>
                      <span className="text-gray-600">Committed: </span>
                      <span className="text-white font-mono">{formatINR(cancelModal.amounts?.totalCommitment)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Paid: </span>
                      <span className="text-[#4ADE80] font-mono">{formatINR(cancelModal.amounts?.capitalPaid || 0)}</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-[#4ADE80]/5 border border-[#4ADE80]/20 mb-4 text-xs text-[#4ADE80]">
                  Refund amount: {formatINR(cancelModal.amounts?.capitalPaid || 0)} (capital paid to date)
                </div>

                <div className="mb-4">
                  <label className="text-xs text-gray-500 block mb-1.5">Reason for cancellation *</label>
                  <select
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm p-2.5 focus:border-white/30 outline-none"
                    data-testid="cancel-reason-select"
                  >
                    <option value="">Select reason...</option>
                    <option value="financial_constraints">Financial constraints</option>
                    <option value="changed_mind">Changed my mind</option>
                    <option value="reduce_commitment">Want to reduce commitment size</option>
                    <option value="personal_reasons">Personal reasons</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-gray-500 block mb-1.5">Additional details (optional)</label>
                  <textarea
                    value={cancelDetails}
                    onChange={(e) => setCancelDetails(e.target.value)}
                    placeholder="Any details to help us process your request..."
                    className="w-full bg-white/5 border border-white/10 text-white text-sm p-2.5 h-20 resize-none focus:border-white/30 outline-none"
                    data-testid="cancel-details-textarea"
                  />
                </div>

                <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 mb-5 text-xs text-yellow-400/80">
                  Your signed documents will be voided as they were never fully executed by the platform.
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelModal(null)}
                    disabled={cancelSubmitting}
                    className="flex-1 py-2.5 border border-white/10 text-gray-400 text-sm hover:text-white hover:border-white/20 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={submitCancellation}
                    disabled={!cancelReason || cancelSubmitting}
                    className="flex-1 py-2.5 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelSubmitting && <Loader2 className="w-3 h-3 animate-spin" />}
                    Submit Cancellation
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
